import { useEffect } from 'react';

interface JsonLdProps {
  data: object;
}

const JsonLd = ({ data }: JsonLdProps) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
};

export default function PillarPageJsonLd() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://business-training.by';
  const pageUrl = `${siteUrl}/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie`;

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Главная",
        "item": { "@id": siteUrl, "name": "Главная" }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Энциклопедия корпоративного обучения",
        "item": { "@id": siteUrl + "/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie", "name": "Энциклопедия корпоративного обучения" }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Что такое корпоративное обучение сотрудников",
        "item": { "@id": pageUrl, "name": "Что такое корпоративное обучение сотрудников" }
      }
    ]
  };

  const articleData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Что такое корпоративное обучение сотрудников | Полное руководство для компаний",
    "description": "Что такое корпоративное обучение сотрудников? Виды обучения персонала, преимущества, современные методы, этапы внедрения и оценка эффективности. Полное руководство для руководителей и HR.",
    "author": {
      "@type": "Person",
      "name": "Алексей Сивко",
      "url": "https://sivko.by/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Business Training",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/61a59271a80ff4d07ef88e35462987b3.png"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2026-07-15",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    },
    "image": "https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/d5f5f47c7d94e3dc67c5ef073553230f.jpeg",
    "inLanguage": "ru-RU",
    "about": {
      "@type": "Thing",
      "name": "Корпоративное обучение сотрудников"
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Что такое корпоративное обучение сотрудников?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Корпоративное обучение — это система развития профессиональных знаний, навыков и компетенций сотрудников компании, направленная на достижение бизнес-целей организации. Это комплексный подход к развитию персонала, включающий тренинги, семинары, воркшопы, бизнес-игры, наставничество, коучинг и другие форматы."
        }
      },
      {
        "@type": "Question",
        "name": "Чем обучение отличается от тренинга?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Тренинг — это один из форматов корпоративного обучения, сфокусированный на отработке конкретных навыков через практические упражнения. Корпоративное обучение — более широкое понятие, включающее все форматы развития сотрудников: от тренингов и семинаров до наставничества, коучинга и онлайн-курсов."
        }
      },
      {
        "@type": "Question",
        "name": "Когда компании нужно корпоративное обучение?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Корпоративное обучение необходимо, когда: снижается конверсия продаж, растёт текучесть кадров, появляются новые продукты, компания масштабируется, сотрудники не справляются с новыми задачами, падает вовлечённость, происходят изменения на рынке. Обучение должно быть системным процессом, а не разовой акцией."
        }
      },
      {
        "@type": "Question",
        "name": "Как понять, что сотрудники нуждаются в обучении?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Признаки: снижение KPI, рост ошибок, конфликты между отделами, низкая конверсия, невыполнение планов, жалобы клиентов, высокая текучесть кадров, низкая вовлечённость. Для точной диагностики проводится аудит компетенций и анализ бизнес-показателей."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли обучать сотрудников онлайн?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да, онлайн-обучение — эффективный формат корпоративного обучения для распределённых команд. Современные платформы позволяют проводить интерактивные тренинги, бизнес-игры и воркшопы онлайн. Смешанное обучение сочетает очные и дистанционные форматы для максимальной эффективности."
        }
      },
      {
        "@type": "Question",
        "name": "Как выбрать программу корпоративного обучения?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Выбор программы начинается с диагностики потребностей бизнеса: анализ KPI, аудит компетенций, опросы сотрудников. На основе данных формируется индивидуальная программа, которая может включать тренинги по продажам, переговорам, управлению, soft skills или комплексное развитие команды."
        }
      },
      {
        "@type": "Question",
        "name": "Сколько длится корпоративное обучение?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Продолжительность зависит от целей и формата. Тренинг может длиться 1-3 дня, комплексная программа развития — от нескольких недель до нескольких месяцев. Важно, чтобы обучение было не разовым событием, а системным процессом с этапами внедрения и сопровождения."
        }
      },
      {
        "@type": "Question",
        "name": "Как оценить эффективность обучения?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Эффективность оценивается по модели Киркпатрика: реакция участников, усвоение знаний, изменение поведения, бизнес-результаты. Ключевые метрики: конверсия, средний чек, удовлетворённость клиентов, повторные продажи, снижение ошибок, вовлечённость сотрудников."
        }
      },
      {
        "@type": "Question",
        "name": "Что такое смешанное обучение (blended learning)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Смешанное обучение — это сочетание очных форматов с онлайн-инструментами: вебинары, видеоуроки, интерактивные задания. Такой подход позволяет охватить больше сотрудников, снизить затраты и поддерживать непрерывность развития персонала."
        }
      },
      {
        "@type": "Question",
        "name": "Какие бывают форматы корпоративного обучения?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Основные форматы: тренинги, семинары, воркшопы, бизнес-игры, фасилитация, наставничество, коучинг, онлайн-обучение, смешанное обучение, микрообучение. Выбор формата зависит от целей обучения, специфики бизнеса и уровня сотрудников."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли обучать руководителей?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да, развитие управленческих компетенций — одно из ключевых направлений корпоративного обучения. Программы для руководителей включают стратегическое мышление, управление командой, делегирование, эффективную коммуникацию, принятие решений и мотивацию сотрудников."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли адаптировать программу под специфику компании?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да, все программы корпоративного обучения адаптируются под отрасль, размер компании, уровень сотрудников и конкретные бизнес-задачи. Мы проводим предварительную диагностику и аудит, чтобы программа была максимально релевантной и эффективной."
        }
      }
    ]
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Business Training",
    "url": siteUrl,
    "logo": "https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/61a59271a80ff4d07ef88e35462987b3.png",
    "description": "Корпоративное обучение сотрудников в Минске, Беларуси, России и Казахстане",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Минск",
      "addressCountry": "BY"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "53.9006",
      "longitude": "27.5590"
    },
    "areaServed": ["BY", "RU", "KZ"],
    "sameAs": ["https://t.me/a_sivko", "https://sivko.by/"]
  };

  const webPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Что такое корпоративное обучение сотрудников | Полное руководство для компаний",
    "description": "Что такое корпоративное обучение сотрудников? Виды обучения персонала, преимущества, современные методы, этапы внедрения и оценка эффективности. Полное руководство для руководителей и HR.",
    "url": pageUrl,
    "inLanguage": "ru-RU",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Business Training",
      "url": siteUrl
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Главная",
          "item": { "@id": siteUrl, "name": "Главная" }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Энциклопедия корпоративного обучения",
          "item": { "@id": siteUrl + "/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie", "name": "Энциклопедия корпоративного обучения" }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Что такое корпоративное обучение сотрудников",
          "item": { "@id": pageUrl, "name": "Что такое корпоративное обучение сотрудников" }
        }
      ]
    }
  };

  return (
    <>
      <JsonLd data={breadcrumbData} />
      <JsonLd data={articleData} />
      <JsonLd data={faqData} />
      <JsonLd data={organizationData} />
      <JsonLd data={webPageData} />
    </>
  );
}